import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API } from 'aws-amplify';
import { FormService } from './form.service';
import { FormGroup } from '@angular/forms';
import { DEFAULT_GET_LIMIT } from '../shared/constants/operationals';
import { LongRange } from './long-range.service';
import { SellerInvitationRole } from './seller-invitations.service';

export const COMPANIES_API_NAME: string = 'companies';
export const COMPANIES_API_ENDPOINT: string = environment.API_BASE_PATH + '/company';

@Injectable({
  providedIn: 'root',
})
export class CompaniesService {
  constructor(private form_service: FormService) {}

  buildCompanyFromForm(form_group: FormGroup, min_revenue: number, max_revenue: number): Company {
    return {
      business_name: form_group.get(this.form_service.business_name_label)?.value,
      vat: form_group.get(this.form_service.vat_label)?.value,
      min_revenue,
      max_revenue,
      website: form_group.get(this.form_service.website_label)?.value,
    };
  }

  listCompanies(list_params?: ListCompaniesParams): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = '';

    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    if (list_params?.status) query_string_params['status'] = list_params.status;
    if (list_params?.business_name) query_string_params['business_name'] = list_params.business_name;
    if (list_params?.vat) query_string_params['vat'] = list_params.vat;
    if (list_params?.company_id) query_string_params['company_id'] = list_params.company_id;
    if (list_params?.sales_network_id) query_string_params['sales_network_id'] = list_params.sales_network_id;
    if (list_params?.agreement) query_string_params['agreement'] = list_params.agreement;
    if (list_params?.product_category) query_string_params['product_category'] = list_params.product_category;
    if (list_params?.origin) query_string_params['origin'] = list_params.origin;

    const init = { response: true, queryStringParameters: query_string_params };
    return from(API.get(api_name, path, init));
  }

  listArchivedCompanies(list_params?: ListArchivedCompaniesParams): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = '/archived';

    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    if (list_params?.business_name) query_string_params['business_name'] = list_params.business_name;
    if (list_params?.vat) query_string_params['vat'] = list_params.vat;
    if (list_params?.company_id) query_string_params['company_id'] = list_params.company_id;

    const init = { response: true, queryStringParameters: query_string_params };
    return from(API.get(api_name, path, init));
  }

  listCompanyMembership(company_id: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/company-membership`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  readSummary(): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = '/summary';
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  edit(company_id: string, company: Company): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}`;
    const init = { response: true, body: company };
    return from(API.put(api_name, path, init));
  }

  archive(company_id: string, note: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/archive`;
    const init = { response: true, body: { note } };
    return from(API.put(api_name, path, init));
  }

  setSalesNetworkId(company_id: string, sales_network_id: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/sales-network-id`;
    const init = { response: true, body: { sales_network_id } };
    return from(API.put(api_name, path, init));
  }

  setProductCategory(company_id: string, product_category: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/product-category`;
    const init = { response: true, body: { product_category } };
    return from(API.put(api_name, path, init));
  }

  setOrigin(company_id: string, origin: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/origin`;
    const init = { response: true, body: { origin } };
    return from(API.put(api_name, path, init));
  }

  setArchivedNote(company_id: string, archived_note: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/archived-note`;
    const init = { response: true, body: { archived_note: archived_note } };
    return from(API.put(api_name, path, init));
  }

  setStandByNote(company_id: string, stand_by_notes: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/stand-by-note`;
    const init = { response: true, body: { stand_by_notes } };
    return from(API.put(api_name, path, init));
  }

  setAgreement(company_id: string, agreement: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/agreement`;
    const init = { response: true, body: { agreement } };
    return from(API.put(api_name, path, init));
  }

  listCompanyAgreements(): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/list-agreements`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  editSettings(company_id: string, settings: CompanySettings): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/settings`;
    const init = { response: true, body: settings };
    return from(API.put(api_name, path, init));
  }

  setCustomSubdomain(company_id: string, custom_subdomain: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/set-custom-subdomain`;
    const init = { response: true, body: { custom_subdomain } };
    return from(API.put(api_name, path, init));
  }

  readCompany(company_id: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  setLongRanges(company_id: string, long_ranges: LongRange[]): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/set-long-range`;
    const init = {
      response: true,
      body: { long_ranges },
    };
    return from(API.put(api_name, path, init));
  }

  setOrderRequirements(company_id: string, order_requirements: OrderCreationRequirements): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/order-requirements`;
    const init = { response: true, body: { order_requirements } };
    return from(API.put(api_name, path, init));
  }

  enable(company_id: string, is_enabled: boolean, is_partially_enabled: boolean): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/enable`;
    const init = {
      response: true,
      body: { is_enabled, is_partially_enabled },
    };
    return from(API.put(api_name, path, init));
  }

  setSalesReferenceEmail(company_id: string, sales_reference_email: string): Observable<any> {
    const api_name: string = COMPANIES_API_NAME;
    const path: string = `/${company_id}/sales-reference-email`;
    const init = { response: true, body: { sales_reference_email } };
    return from(API.put(api_name, path, init));
  }
}

export interface ListCompaniesParams {
  company_id?: string;
  business_name?: string;
  vat?: string;
  status?: CompanyStatus;
  sales_network_id?: string;
  product_category?: string;
  agreement?: string;
  origin?: string;

  cursor?: string | null;
  limit?: number;
}

export interface ListArchivedCompaniesParams {
  company_id?: string;
  business_name?: string;
  vat?: string;

  cursor?: string | null;
  limit?: number;
}

export interface Company {
  company_id?: string;
  owner_id?: string;
  object?: string;
  status?: CompanyStatus;
  business_name: string;
  vat: string;
  min_revenue: number;
  max_revenue: number;
  website?: string;
  sales_network_id?: string;
  is_sales_network_id_editing_disabled?: boolean;
  product_category?: CompanyProductCategory;
  settings?: CompanySettings;
  long_ranges?: LongRange[];
  order_requirements?: OrderCreationRequirements;
  custom_buyer_subdomain?: string;
  archived_note?: string;
  agreement?: string;
  is_enabled?: boolean;
  is_partially_enabled?: boolean;
  sales_reference_email?: string;
  created_at?: number;
  updated_at?: number;
  archived_by?: string;
  tax_code?: string;
  sales_channel?: string;
  owner_email?: string;
  owner_firstname?: string;
  owner_lastname?: string;
  allowed_domains?: string[];
  origin?: CompanyOrigin;
  stand_by_note?: string;
}

export interface CompanySummary {
  length: number;
  pending_evaluation: number;
  pending_signature: number;
  pending_acceptance: number;
  accepted: number;
  denied: number;
  archived: number;
}

export interface CompanySettings {
  is_store_payment_method_enabled?: boolean;
  is_iban_refund_enabled?: boolean;
  is_ext_order_id_enabled?: boolean;
  is_tax_deduction_enabled?: boolean;
  is_tax_date_and_number_enabled?: boolean;
  allowed_deduction_types?: AllowedDeductionType[];
  is_custom_domains_enabled?: boolean;
  is_prepaid_cards_blocked?: boolean;
  is_prepaid_cards_blocked_on_long?: boolean;
  is_prepaid_cards_blocked_on_ecommerce?: boolean;
  is_fourthline_onboarding_enabled?: boolean;
}

export enum AllowedDeductionType {
  bonus_mobili = 'bonus_mobili',
  conto_termico = 'conto_termico',
  conto_termico_3 = 'conto_termico_3',
  interventi_sismici = 'interventi_sismici',
  riqualifica_edilizia = 'riqualifica_edilizia',
  risparmio_energetico = 'risparmio_energetico',
}

export enum CompanyStatus {
  pending_evaluation = 'pending_evaluation',
  pending_signature = 'pending_signature',
  pending_acceptance = 'pending_acceptance',
  accepted = 'accepted',
  partially_enabled = 'partially_enabled',
  denied = 'denied',
  archived = 'archived',
}

export enum CompanyProductCategory {
  clothing = 'clothing',
  furniture = 'furniture',
  musical_instruments = 'musical_instruments',
  sporting_goods = 'sporting_goods',
  cosmetics_beauty = 'cosmetics_beauty',
  opticians = 'opticians',
  professional_services = 'professional_services',
  dental_clinics = 'dental_clinics',
  medical_practices = 'medical_practices',
  veterinarians = 'veterinarians',
  tv_telephony_and_electronics = 'tv_telephony_and_electronics',
  other = 'other',

  //new
  real_estate_agencies = 'real_estate_agencies',
  hearing_aids = 'hearing_aids',
  home_and_bathroom_furniture = 'home_and_bathroom_furniture',
  insurance = 'insurance',
  bicycles = 'bicycles',
  bodywork = 'bodywork',
  furniture_accessories = 'furniture_accessories',
  dealers = 'dealers',
  door_to_door = 'door_to_door',
  diy_and_home_improvement = 'diy_and_home_improvement',
  physiotherapy_osteopathy = 'physiotherapy_osteopathy',
  training_and_courses = 'training_and_courses',
  tire_dealers = 'tire_dealers',
  home_alarm_systems = 'home_alarm_systems',
  car_alarm_systems = 'car_alarm_systems',
  car_motorcycle_repair_shops = 'car_motorcycle_repair_shops',
  gyms = 'gyms',
  bed_bases_and_mattresses = 'bed_bases_and_mattresses',
  energy_renovation = 'energy_renovation',
  doors_and_windows = 'doors_and_windows',
  stoves_and_fireplaces = 'stoves_and_fireplaces',
  leisure = 'leisure',
}

export enum CompanyAgreementCategory {
  kenovo = 'Kenovo',
  pensare_casa = 'Pensarecasa',
  moretti_compact = 'Moretti compact',
  figurella = 'Figurella',
  privacar = 'Privacar',
  cmt_moto = 'CMT Moto',
  verisure = 'Verisure',
  edison = 'Edison',
  dante = 'Dante',
  lifenet = 'LifeNet',
  vitha_group = 'Vitha Group',
}

export enum CompanyOrigin {
  loans = 'loans',
  mtp = 'mtp',
  direct = 'direct',
}

export class OrderCreationRequirements {
  is_bnpl_long_enabled?: boolean;
  is_sdd_payment_enabled?: boolean;
  min_amount?: number;
  max_amount?: number;
}

export interface CompanyMembership {
  seller_id?: string;
  company_id?: string;
  company_business_name?: string;
  seller_sub?: string;
  seller_firstname?: string;
  seller_lastname?: string;
  seller_email?: string;
  seller_role?: SellerInvitationRole;
  object?: string;
  created_at?: number;
  updated_at?: number;
  is_seller_confirmed?: boolean;
  seller_password_set_at?: number;
}
